import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Fingerprint, MailCheck, MailWarning } from "lucide-react";
import { Spinner } from "@/components/common/FullScreenLoader";
import { BoolBadge } from "@/components/shell/Badges";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

export function MfaStatusCard() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/auth/mfa/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, [user.mfa_enabled]);
  const required = user.user_type === "staff";
  return (
    <section className="bz-card" data-testid="security-mfa-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="bz-caption">Multi-factor authentication</p>
          <div className="mt-3 flex items-center gap-3">
            <Fingerprint className={`h-6 w-6 ${user.mfa_enabled ? "text-electric" : "text-slate-500"}`} strokeWidth={1.75} />
            <BoolBadge value={user.mfa_enabled} yes="Enabled" no={required ? "Required" : "Not enabled"} testId="security-mfa-status" />
          </div>
        </div>
        {!user.mfa_enabled ? (
          <Link to="/mfa/setup" data-testid="security-mfa-setup-link" className="bz-btn-primary text-xs">
            Set up authenticator
          </Link>
        ) : null}
      </div>
      <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs text-slate-500">Enabled since</dt>
          <dd className="text-slate-200">{formatDateTime(status?.enabled_at)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Recovery codes remaining</dt>
          <dd data-testid="security-mfa-recovery-remaining" className="font-mono text-slate-200">{status ? status.recovery_codes_remaining : "—"}</dd>
        </div>
      </dl>
      <p className="mt-4 text-xs text-slate-500">
        {required ? "Staff identities cannot disable MFA. No permanent “remember this device” bypass exists." : "Optional for customers, strongly recommended."}
      </p>
    </section>
  );
}

export function EmailVerificationCard() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function request() {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/email/verify/request");
      if (data.status === "already_verified") {
        await refresh();
        return;
      }
      setSent(true);
      toast.success("Verification email queued (mock adapter)");
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bz-card" data-testid="security-email-card">
      <p className="bz-caption">Email address</p>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {user.email_verified ? <MailCheck className="h-6 w-6 text-emerald-400" strokeWidth={1.75} /> : <MailWarning className="h-6 w-6 text-amber-400" strokeWidth={1.75} />}
          <div>
            <p className="font-mono text-sm text-white">{user.email}</p>
            <BoolBadge value={user.email_verified} yes="Verified" no="Unverified" testId="security-email-status" />
          </div>
        </div>
        {!user.email_verified ? (
          <button type="button" onClick={request} disabled={busy || sent} data-testid="security-verify-email-btn" className="bz-btn-secondary text-xs">
            {busy ? <Spinner className="h-3.5 w-3.5" /> : null}
            {sent ? "Link sent" : "Send verification link"}
          </button>
        ) : null}
      </div>
      {sent ? (
        <p data-testid="security-verify-email-sent" className="mt-4 text-xs text-slate-500">
          A verification link valid for 24 hours was queued via the mock adapter. No real email is sent in Phase 0.
        </p>
      ) : null}
    </section>
  );
}
